import { useState, useEffect } from 'react';
import { Team, Venue, GroupMatch, KnockoutMatch, TournamentData, Group, GROUPS } from '../types/tournament';

interface RawTeamsData {
  teams: Team[];
}

interface RawVenuesData {
  venues: Venue[];
}

interface RawMatchesData {
  groupMatches: GroupMatch[];
  knockoutMatches: KnockoutMatch[];
}

export function useBracketData() {
  const [data, setData] = useState<TournamentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadData() {
      try {
        const [teamsRes, venuesRes, matchesRes] = await Promise.all([
          fetch('/data/teams.json'),
          fetch('/data/venues.json'),
          fetch('/data/matches.json'),
        ]);

        if (!teamsRes.ok || !venuesRes.ok || !matchesRes.ok) {
          throw new Error('Failed to load tournament data');
        }

        const teamsData: RawTeamsData = await teamsRes.json();
        const venuesData: RawVenuesData = await venuesRes.json();
        const matchesData: RawMatchesData = await matchesRes.json();

        // Build groups from teams
        const groups: Group[] = GROUPS.map(groupLetter => {
          const groupTeams = teamsData.teams
            .filter(t => t.group === groupLetter)
            .map(t => t.id);

          const groupMatchIds = matchesData.groupMatches
            .filter(m => m.group === groupLetter)
            .map(m => m.id);

          return {
            id: groupLetter,
            name: `Group ${groupLetter}`,
            teams: groupTeams,
            matches: groupMatchIds,
            standings: groupTeams.map((teamId, index) => ({
              teamId,
              played: 0,
              won: 0,
              drawn: 0,
              lost: 0,
              goalsFor: 0,
              goalsAgainst: 0,
              goalDifference: 0,
              points: 0,
              position: index + 1,
            })),
          };
        });

        setData({
          teams: teamsData.teams,
          venues: venuesData.venues,
          groups,
          groupMatches: matchesData.groupMatches,
          knockoutMatches: matchesData.knockoutMatches,
        });
        setLoading(false);
      } catch (e) {
        console.error('Error loading data:', e);
        setError(e instanceof Error ? e.message : 'Unknown error');
        setLoading(false);
      }
    }

    loadData();
  }, []);

  return { data, loading, error };
}

export function getTeamById(teams: Team[], id: string): Team | undefined {
  return teams.find(t => t.id === id);
}

export function getVenueById(venues: Venue[], id: string): Venue | undefined {
  return venues.find(v => v.id === id);
}

export function formatMatchDate(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatMatchTime(time: string): string {
  if (!time || time === 'TBD') return 'TBD';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}
